/**
 * Entrada de la aplicación.
 *
 * El orden de los `import` de aquí importa y no es estético:
 *
 * 1. `boot/oauth-callback.ts` limpia `?code=&state=` de la URL y deja `?intro=0`
 *    en su lugar. Tiene que ocurrir antes de nada.
 * 2. `main.js` lee `?intro=0` en el momento de evaluarse. Si el paso anterior no
 *    ha pasado ya, quien vuelve de autorizar en Notion se come la intro entera
 *    otra vez. Los módulos ES se evalúan en el orden en que se importan, así que
 *    ese orden es el mecanismo.
 *
 * `main.js` sigue siendo JavaScript sin tipos y no se toca: está calibrado y
 * cualquier reescritura arriesga la coreografía.
 */

import { oauthCallback } from "./boot/oauth-callback.ts";
import "./main.js";

import { exchangeCode, OAuthError } from "./core/notion/oauth.ts";
import { openSession } from "./core/persist/session.ts";
import { mountAssistant } from "./ui/assistant/assistant.ts";
import { mountDock } from "./ui/dock/dock.ts";
import { notionExchangeFailed, notionExchangeStarted } from "./ui/dock/workspace.ts";
import { mountBegin } from "./ui/start/begin.ts";
import { mountEntry } from "./ui/start/entry.ts";
import { mountWriter } from "./ui/writer/writer.ts";
import { startTheme } from "./core/state/theme.ts";

export interface IntentSubmitDetail {
  /** Lo que la persona escribió o dictó, ya recortado. */
  intent: string;
  /** ISO 8601. */
  at: string;
}

declare global {
  interface DocumentEventMap {
    "intent:submit": CustomEvent<IntentSubmitDetail>;
  }
}

/**
 * El tema. La primera pintada ya la resolvió el script suelto de `index.html`
 * —los módulos van diferidos y llegarían tarde—; esto engancha lo que hace falta
 * después: repintar cuando la persona lo cambia en la bandeja y seguir al
 * sistema mientras esté en «automático».
 */
startTheme();

const dock = mountDock();
mountAssistant();

/**
 * El documento. Se monta escuchando qué página está abierta y no se ve hasta que
 * hay una: abrir un documento es elegirlo en la franja, así que el árbol no
 * necesita saber que existe un editor ni el editor cómo se llega a él.
 */
mountWriter();

/**
 * La entrada: el campo de la intención. `main.js` la anunciaba y no había nadie
 * escuchando; ahora se recuerda —es el primer contexto del asistente—, se puede
 * dictar y abre la pantalla que crea el proyecto en Notion con sus tres documentos.
 *
 * La hoja se monta antes que el campo: es quien guarda cómo se abre, y el campo la
 * abre en cuanto se envía una intención.
 */
mountBegin({ openDock: () => { dock.open(); } });
mountEntry();

/**
 * Cierre del viaje de OAuth. La página se ha recargado por completo desde que
 * empezó, así que lo único que queda de aquel gesto es el código en la URL —ya
 * retirado— y el `state` en `sessionStorage`.
 */
async function resolveOAuth(): Promise<void> {
  switch (oauthCallback.kind) {
    case "none":
      return;

    case "denied":
      notionExchangeFailed(oauthCallback.message);
      dock.open();
      return;

    case "bad-state":
      // O el `state` no cuadra, o se llegó aquí con un `code` que esta pestaña
      // nunca pidió. Se descarta sin canjearlo: es exactamente el caso que el
      // `state` existe para detectar.
      notionExchangeFailed(
        "La respuesta de Notion no correspondía a esta petición. No se completó " +
        "la conexión; vuelve a intentarlo desde aquí.",
      );
      dock.open();
      return;

    case "code":
      notionExchangeStarted();
      dock.open();
      try {
        openSession(await exchangeCode(oauthCallback.code));
      } catch (cause) {
        notionExchangeFailed(
          cause instanceof OAuthError
            ? cause.message
            : "No se pudo completar la conexión con Notion.",
        );
      }
  }
}

void resolveOAuth();
